<template>
  <div>
    <!-- No analysis yet -->
    <div v-if="!analysis && !running" class="text-center py-8">
      <p class="text-gray-500 dark:text-gray-400 mb-4">
        Analyze the SERP for "<strong>{{ selectedKeyword }}</strong>" to understand search intent, content format, and feature opportunities.
      </p>
      <button @click="startAnalysis" class="inline-flex items-center px-4 py-2 bg-[#12e40b] hover:bg-[#0fd609] text-white rounded-lg font-medium text-sm transition-colors">
        Analyze SERP
      </button>
    </div>

    <!-- Progress -->
    <div v-if="running" class="mb-6">
      <div class="flex items-center gap-3 mb-2">
        <div class="animate-spin h-5 w-5 border-2 border-primary-500 border-t-transparent rounded-full" />
        <span class="text-sm text-gray-600 dark:text-gray-400">{{ stepMessage }}</span>
      </div>
      <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
        <div class="bg-primary-500 h-2 rounded-full transition-all duration-500" :style="{ width: progressWidth }" />
      </div>
    </div>

    <!-- Error -->
    <div v-if="analysis && analysis.status === 'failed'" class="rounded-xl bg-red-50 dark:bg-red-900/20 p-4 mb-4">
      <p class="text-sm text-red-700 dark:text-red-400">
        {{ analysis.error_message }}
      </p>
      <button @click="startAnalysis" class="btn btn-secondary btn-sm mt-2">
        Retry
      </button>
    </div>

    <!-- Results -->
    <div v-if="analysis && analysis.status === 'completed'">
      <!-- Keyword mismatch banner: page's selected keyword differs from analysis keyword -->
      <div v-if="analysis.keyword_changed" class="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4 mb-4">
        <div class="flex items-start gap-3">
          <svg class="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01M5.07 19h13.86a2 2 0 001.74-3L13.74 4a2 2 0 00-3.48 0L3.33 16a2 2 0 001.74 3z" />
          </svg>
          <div class="flex-1">
            <p class="text-sm text-amber-800 dark:text-amber-300 mb-1">
              Selected keyword changed to <strong>"{{ selectedKeyword }}"</strong>.
            </p>
            <p class="text-xs text-amber-700 dark:text-amber-400 mb-3">
              This analysis is for "{{ analysis.keyword }}". Re-running will discard your competitor analysis, keyword strategy, and other downstream work.
            </p>
            <button
              @click="runReanalysis"
              :disabled="running"
              class="inline-flex items-center px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium text-xs transition-colors disabled:opacity-50">
              {{ running ? 'Starting...' : 'Re-analyze for new keyword' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Confirmed banner -->
      <div v-if="analysis.confirmed" class="rounded-xl bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 p-4 mb-6">
        <div class="flex items-center gap-2">
          <span class="text-primary-600 dark:text-primary-400 font-semibold text-sm uppercase tracking-wide">SERP Analysis Confirmed</span>
          <span class="text-sm text-gray-500 dark:text-gray-400">{{ analysis.intent_classification }} / {{ analysis.content_format }}</span>
        </div>
      </div>

      <!-- Intent Classification -->
      <div class="rounded-xl border border-gray-200 dark:border-gray-700 p-5 mb-4">
        <h4 class="text-sm font-medium font-heading text-gray-900 dark:text-gray-100 mb-3">
          Search Intent
        </h4>
        <div class="flex items-center gap-3 mb-3">
          <span :class="intentBadgeClass" class="px-3 py-1 rounded-full text-sm font-medium capitalize">
            {{ analysis.intent_classification }}
          </span>
        </div>
        <ul v-if="intentEvidence.length" class="space-y-1">
          <li v-for="(ev, i) in intentEvidence" :key="i" class="text-sm text-gray-600 dark:text-gray-400">
            <span class="text-gray-400 mr-1">&bull;</span>
            {{ ev.signal }}
            <span class="text-xs text-gray-400 ml-1">({{ ev.supports }})</span>
          </li>
        </ul>
      </div>

      <!-- Content Format -->
      <div class="rounded-xl border border-gray-200 dark:border-gray-700 p-5 mb-4">
        <h4 class="text-sm font-medium font-heading text-gray-900 dark:text-gray-100 mb-3">
          Dominant Content Format
        </h4>
        <span class="px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-sm font-medium capitalize">
          {{ analysis.content_format }}
        </span>
        <div v-if="analysis.format_mismatch_warning" class="mt-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3">
          <p class="text-sm text-amber-700 dark:text-amber-400">
            {{ analysis.format_mismatch_warning }}
          </p>
        </div>
      </div>

      <!-- SERP Features Grid -->
      <div class="rounded-xl border border-gray-200 dark:border-gray-700 p-5 mb-4">
        <h4 class="text-sm font-medium font-heading text-gray-900 dark:text-gray-100 mb-3">
          SERP Features
        </h4>
        <div class="grid grid-cols-2 lg:grid-cols-3 gap-3">
          <div
            v-for="feature in serpFeatures"
            :key="feature.type"
            :class="[
              'rounded-lg p-3 border text-sm',
              feature.detected
                ? 'border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/20'
                : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 opacity-50'
            ]">
            <div class="font-medium mb-1">
              {{ formatFeatureType(feature.type) }}
              <span v-if="feature.detected" class="text-primary-600 ml-1">&#10003;</span>
              <span v-else class="text-gray-400 ml-1">&#10007;</span>
            </div>
            <p v-if="feature.detected && feature.optimization_note" class="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
              {{ feature.optimization_note }}
            </p>
          </div>
        </div>
      </div>

      <!-- PAA Questions -->
      <div v-if="paaQuestions.length" class="rounded-xl border border-gray-200 dark:border-gray-700 p-5 mb-4">
        <h4 class="text-sm font-medium font-heading text-gray-900 dark:text-gray-100 mb-3">
          People Also Ask ({{ paaQuestions.length }})
        </h4>
        <ul class="space-y-2">
          <li v-for="(q, i) in paaQuestions" :key="i" class="text-sm">
            <span class="text-gray-700 dark:text-gray-300 font-medium">{{ q.question }}</span>
            <p v-if="q.snippet" class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {{ q.snippet }}
            </p>
          </li>
        </ul>
      </div>

      <!-- AI Overview -->
      <div v-if="analysis.ai_overview && analysis.ai_overview.present" class="rounded-xl border border-gray-200 dark:border-gray-700 p-5 mb-4">
        <h4 class="text-sm font-medium font-heading text-gray-900 dark:text-gray-100 mb-3">
          AI Overview
        </h4>
        <p v-if="analysis.ai_overview.text" class="text-sm text-gray-600 dark:text-gray-400 mb-2">
          {{ analysis.ai_overview.text }}
        </p>
        <div v-if="analysis.ai_overview.sources && analysis.ai_overview.sources.length" class="mt-2">
          <p class="text-xs text-gray-500 font-medium mb-1">
            Cited Sources:
          </p>
          <ul class="space-y-1">
            <li v-for="(src, i) in analysis.ai_overview.sources" :key="i" class="text-xs text-gray-500">
              {{ src.title }} <span class="text-gray-400">({{ src.domain }})</span>
            </li>
          </ul>
        </div>
      </div>

      <!-- Auto-confirmed notice or manual confirm button -->
      <div v-if="analysis.confirmed" class="flex items-center justify-between mt-6">
        <div class="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
          <span>&#10003;</span>
          <span>Analysis confirmed — Competitors tab is unlocked.</span>
        </div>
        <button
          v-if="!showRerunConfirm"
          @click="showRerunConfirm = true"
          :disabled="running"
          class="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 underline disabled:opacity-50">
          Re-analyze
        </button>
      </div>
      <div v-else class="flex justify-end mt-6">
        <button
          @click="confirmAnalysis"
          :disabled="confirming"
          class="inline-flex items-center px-4 py-2 bg-[#12e40b] hover:bg-[#0fd609] text-white rounded-lg font-medium text-sm transition-colors disabled:opacity-50">
          {{ confirming ? 'Confirming...' : 'Confirm Analysis & Proceed to Competitors' }}
        </button>
      </div>

      <!-- Inline re-run confirmation -->
      <div v-if="showRerunConfirm" class="mt-4 rounded-xl border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-4">
        <p class="text-sm text-amber-800 dark:text-amber-300 mb-3">
          Re-analyze SERP? This will discard your competitor analysis, keyword strategy, and other downstream work.
        </p>
        <div class="flex justify-end gap-2">
          <button
            @click="showRerunConfirm = false"
            class="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            Cancel
          </button>
          <button
            @click="reanalyzeFromConfirm"
            :disabled="running"
            class="inline-flex items-center px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium text-sm disabled:opacity-50">
            {{ running ? 'Starting...' : 'Re-analyze' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import consumer from '~/channels/consumer'
import { startSerpAnalysis, getSerpAnalysis, confirmSerpAnalysis } from '~/javascript/api/serp_analysis.js'

const props = defineProps({
  pageId: { type: String, required: true },
  selectedKeyword: { type: String, default: null },
  selectedCandidateId: { type: String, default: null }
})

const emit = defineEmits(['confirmed'])

const analysis = ref(null)
const running = ref(false)
const confirming = ref(false)
const showRerunConfirm = ref(false)
const currentStep = ref('')
const stepMessage = ref('')
let subscription = null
let pollTimer = null

const STEPS = ['fetching', 'detecting', 'classifying', 'completed']

const progressWidth = computed(() => {
  const idx = STEPS.indexOf(currentStep.value)
  if (idx < 0) return '0%'
  return `${((idx + 1) / STEPS.length) * 100}%`
})

const serpFeatures = computed(() => {
  if (!analysis.value?.serp_features?.features) return []
  return analysis.value.serp_features.features
})

const paaQuestions = computed(() => analysis.value?.paa_questions || [])

const intentEvidence = computed(() => {
  const evidence = analysis.value?.intent_evidence
  if (Array.isArray(evidence)) return evidence
  return []
})

const intentBadgeClass = computed(() => {
  const map = {
    informational: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400',
    commercial: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
    transactional: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400',
    mixed: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'
  }
  return map[analysis.value?.intent_classification] || map.mixed
})

const formatFeatureType = (type) => type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

const startAnalysis = async() => {
  running.value = true
  currentStep.value = 'fetching'
  stepMessage.value = 'Starting SERP analysis...'
  try {
    await startSerpAnalysis(props.pageId, props.selectedCandidateId)
    startPolling()
  } catch (e) {
    running.value = false
    console.error('Failed to start SERP analysis:', e)
  }
}

const runReanalysis = async() => {
  // Reset displayed state so the user sees the progress bar instead of the old completed view
  showRerunConfirm.value = false
  analysis.value = null
  running.value = true
  currentStep.value = 'fetching'
  stepMessage.value = 'Starting SERP analysis...'
  try {
    // Pass null so the controller resolves the candidate from page.selected_keyword_candidate_id;
    // the prop can be stale after the user picks a new keyword in the Keywords tab.
    await startSerpAnalysis(props.pageId, null)
    startPolling()
  } catch (e) {
    running.value = false
    if (e.response?.status === 409) {
      // Another run is already in flight; refetch to sync UI
      fetchAnalysis() // eslint-disable-line no-use-before-define
    } else {
      console.error('Failed to start SERP re-analysis:', e) // eslint-disable-line no-console
    }
  }
}

const reanalyzeFromConfirm = async() => {
  showRerunConfirm.value = false
  await runReanalysis()
}

function startPolling() {
  stopPolling()
  pollTimer = setInterval(async() => {
    try {
      const response = await getSerpAnalysis(props.pageId)
      const a = response.data.serp_analysis
      if (a && a.status !== 'running' && a.status !== 'pending') {
        analysis.value = a
        running.value = false
        if (a.confirmed) emit('confirmed')
        stopPolling()
      }
    } catch (_e) { /* ignore polling errors */ }
  }, 5000)
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

const fetchAnalysis = async() => {
  try {
    const response = await getSerpAnalysis(props.pageId)
    analysis.value = response.data.serp_analysis
    if (analysis.value?.status === 'running') {
      running.value = true
    }
    if (analysis.value?.confirmed) {
      emit('confirmed')
    } else if (analysis.value?.status === 'completed' && !analysis.value?.confirmed) {
      // Auto-confirm completed analyses to streamline the pipeline
      autoConfirm()
    }
  } catch (e) {
    console.error('Error fetching SERP analysis:', e) // eslint-disable-line no-console
  }
}

const autoConfirm = async() => {
  try {
    await confirmSerpAnalysis(props.pageId)
    if (analysis.value) analysis.value.confirmed = true
    emit('confirmed')
  } catch (e) {
    // Auto-confirm failed, user can still manually confirm
    console.error('Auto-confirm failed:', e) // eslint-disable-line no-console
  }
}

const confirmAnalysis = async() => {
  confirming.value = true
  try {
    await confirmSerpAnalysis(props.pageId)
    analysis.value.confirmed = true
    emit('confirmed')
  } catch (e) {
    console.error('Error confirming:', e)
  } finally {
    confirming.value = false
  }
}

onMounted(() => {
  fetchAnalysis()
  subscription = consumer.subscriptions.create(
    { channel: 'SerpAnalysisChannel', page_id: props.pageId },
    {
      received(data) {
        currentStep.value = data.step
        stepMessage.value = data.message || ''
        if (data.step === 'completed') {
          running.value = false
          stopPolling()
          fetchAnalysis()
        } else if (data.step === 'failed') {
          running.value = false
          stopPolling()
          fetchAnalysis()
        }
      }
    }
  )
})

onUnmounted(() => {
  subscription?.unsubscribe()
  stopPolling()
})
</script>
